'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSignIn } from '@clerk/nextjs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from '@/features/auth/schemas/auth.schema'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

/**
 * Admin-only self-serve password reset, entirely distinct from staff's (staff have no
 * self-serve reset at all — an admin resets it for them, see `AdminUsersSyncService`).
 *
 * Never reveals whether the email actually matches an admin account — the same "same message
 * either way" policy this app already applies to phone-OTP sign-in (`login-view.tsx`) and the
 * old staff request dialog. `signIn.create`/`resetPasswordEmailCode.sendCode` are called
 * either way and their own `{ error }` is not surfaced; only a genuine thrown exception
 * (network/Clerk outage) gets a distinct retry message.
 */
export function AdminForgotPasswordDialog({ initialEmail }: { initialEmail: string }) {
  const router = useRouter()
  const { signIn } = useSignIn()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onTouched',
    defaultValues: { email: initialEmail },
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true)
    setErrorMessage(null)
    try {
      await signIn.create({ identifier: values.email })
      await signIn.resetPasswordEmailCode.sendCode()
      setOpen(false)
      router.push('/team/reset-password')
    } catch {
      setErrorMessage('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          reset({ email: initialEmail })
          setErrorMessage(null)
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-primary text-sm font-medium underline-offset-4 hover:underline"
        >
          Forgot password?
        </button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              We&apos;ll email a verification code to your admin address if it matches an account.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="forgot-email">Admin email</FieldLabel>
              <Input
                id="forgot-email"
                type="email"
                placeholder="admin@nivex.in"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              <FieldError errors={[errors.email]} />
            </Field>
            {errorMessage && (
              <FieldDescription className="text-red-600">{errorMessage}</FieldDescription>
            )}
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send code'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
